import React, { type ReactElement } from 'react';
import { Text } from '@is24at/react-ui-core';
import { Stars } from './components';
import styles from './Awards.scss';

/**
 * Bump this counter in case of replacing the award images in the S3 bucket,
 * so that any existing browser image cache is invalidated.
 */
const AWARD_IMAGE_INVALIDATION_COUNTER = 1;

const AWARD_IMAGES_LOCATION =
  'https://s3.eu-west-1.amazonaws.com/static.www.immobilienscout24.at/realtor-badges';
const buildImageUrl = (filename: string) =>
  `${AWARD_IMAGES_LOCATION}/${filename}.svg?v=${AWARD_IMAGE_INVALIDATION_COUNTER}`;

export type Award = {
  feature:
    | 'SALES_PROFESSIONAL_AWARD'
    | 'IMMO_EXPERT_FIVE_STARS'
    | 'IMMO_EXPERT_THREE_STARS'
    | 'PREMIUM_PARTNER_AWARD_2023'
    | 'REALTOR_BADGE_GOLD'
    | 'REALTOR_BADGE_SILVER'
    | 'REALTOR_BADGE_BRONZE';
  className: string;
  imageUrl: string;
  imageAlt: string;
  LabelComponent: ReactElement;
};

/**
 * List of awards supported by the application. Note: The list is ordered, the different
 * awards will render according to the ranking established here.
 */
export const Awards: Award[] = [
  {
    feature: 'REALTOR_BADGE_GOLD',
    className: 'realtor-badge-gold',
    imageUrl: buildImageUrl('realtor_badge_gold'),
    imageAlt: 'Gold Partner',
    LabelComponent: <Text bold>Gold</Text>,
  },
  {
    feature: 'SALES_PROFESSIONAL_AWARD',
    className: 'sales-professional-award',
    imageUrl: buildImageUrl('sales_professional_award'),
    imageAlt: 'Sales Professional Award',
    LabelComponent: (
      <Text color="white" bold>
        VERKAUFS
        <span className={styles['award-label__text--teal']}>PROFI</span>
      </Text>
    ),
  },
  {
    feature: 'IMMO_EXPERT_FIVE_STARS',
    className: 'five-stars-award',
    imageUrl: buildImageUrl('immo_expert_five_stars_award'),
    imageAlt: 'Immo Expert ***** Award',
    LabelComponent: (
      <>
        <Stars id="IMMO_EXPERT_FIVE_STARS" amount={5} />
        <Text bold>Experte</Text>
      </>
    ),
  },
  {
    feature: 'REALTOR_BADGE_SILVER',
    className: 'realtor-badge-silver',
    imageUrl: buildImageUrl('realtor_badge_silver'),
    imageAlt: 'Silber Partner',
    LabelComponent: <Text bold>Silber</Text>,
  },
  {
    feature: 'IMMO_EXPERT_THREE_STARS',
    className: 'three-stars-award',
    imageUrl: buildImageUrl('immo_expert_three_stars_award'),
    imageAlt: 'Immo Expert *** Award',
    LabelComponent: (
      <>
        <Stars id="IMMO_EXPERT_THREE_STARS" amount={3} />
        <Text bold>Experte</Text>
      </>
    ),
  },
  {
    feature: 'REALTOR_BADGE_BRONZE',
    className: 'realtor-badge-bronze',
    imageUrl: buildImageUrl('realtor_badge_bronze'),
    imageAlt: 'Bronze Partner',
    LabelComponent: <Text bold>Bronze</Text>,
  },
  {
    feature: 'PREMIUM_PARTNER_AWARD_2023',
    className: 'premium-partner-award',
    imageUrl: buildImageUrl('premium_partner_award_2023'),
    imageAlt: 'Premium Partner Award',
    LabelComponent: <Text bold>Premium</Text>,
  },
];

export const getAwardsByActiveFeatures = (activeFeatures: string[]) =>
  Awards.filter((config) => activeFeatures.includes(config.feature));
